import { useState, useEffect, useContext } from 'react';
import Box from '@mui/material/Box';
import { DataContext } from '../context/dataContext';
import { DataGrid } from '@mui/x-data-grid';
import TextField from '@mui/material/TextField';
import Popup from './Popup';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';

function SortableTable({
  rows,
  columns,
  isLoading,
  isSearch,
  isSearchForReview,
  selectedRows,
  handleDownloadJson,
  handleDownloadImages,
  showErrorMessage,
  selectedScans,
  downloadCSV,
  onSubScanSelect,
}) {
  const [filteredData, setFilteredData] = useState(rows);
  const { searchTerm, setSearchTermData } = useContext(DataContext);
  const [isPopupOpen, setPopupOpen] = useState(false);

  const showSearchBar = isSearch || isSearchForReview;

  const customLocaleText = {
    noRowsLabel: showErrorMessage
      ? 'Too Many Scans! Please select Date Filters'
      : 'There are No Records to display',
  };

  const updatedColumns = columns.map((column) =>
    column.field === 'image'
      ? {
        ...column,
        renderCell: (params) => (
          <img src={params.value} alt="Scan preview" style={{ width: 50, height: 50 }} />
        ),
      }
      : column,
  );

  // Unified filter logic — both modes search scan_id + name
  // isSearch adds email/phone, isSearchForReview adds user_name/updated_by
  useEffect(() => {
    if (!showSearchBar || !searchTerm) {
      setFilteredData(rows);
      return;
    }

    const term = searchTerm.toLowerCase();

    setFilteredData(
      rows.filter((row) => {
        const fullName = `${row?.first_name || ''} ${row?.last_name || ''}`.toLowerCase();
        const scanID = row?.scan_id?.toLowerCase() || '';

        if (isSearch) {
          return (
            scanID.includes(term) ||
            fullName.includes(term) ||
            row?.email?.toLowerCase().includes(term) ||
            row?.phone_number?.toLowerCase().includes(term)
          );
        }

        // isSearchForReview
        return (
          scanID.includes(term) ||
          fullName.includes(term) ||
          row?.user_name?.toLowerCase().includes(term) ||
          row?.updated_by?.toLowerCase().includes(term)
        );
      }),
    );
  }, [searchTerm, rows, isSearch, isSearchForReview, showSearchBar]);

  return (
    <div style={{ width: '100%', height: '100vh' }}>
      <Box sx={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>

        {/* ── Unified search bar — shown for both isSearch and isSearchForReview ── */}
        {showSearchBar && (
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTermData(e.target.value)}
            />

            <div style={{ display: 'flex', gap: 12 }}>
              {/* Download CSV — only for isSearch when rows selected */}
              {isSearch && selectedScans?.length > 0 && (
                <Button variant="contained" className="App-bg-color" onClick={downloadCSV}
                  style={{ fontSize: '0.8rem' }}>
                  Download CSV
                </Button>
              )}

              {/* Download JSON + Image — only for isSearchForReview when rows selected */}
              {isSearchForReview && selectedRows?.length > 0 && (
                <>
                  <Button variant="contained" className="App-bg-color" onClick={handleDownloadJson}
                    style={{ fontSize: '0.8rem' }}>
                    Download JSON
                  </Button>
                  <Button variant="contained" className="App-bg-color" onClick={handleDownloadImages}
                    style={{ fontSize: '0.8rem' }}>
                    Download Image
                  </Button>
                </>
              )}

              <Button variant="contained" className="App-bg-color" onClick={() => setPopupOpen(true)}
                style={{ fontSize: '0.8rem' }}>
                Search By Scan ID
              </Button>
            </div>
          </div>
        )}

        {isPopupOpen && (
          <Popup
            title="Search By ScanID"
            open={isPopupOpen}
            onClose={() => setPopupOpen(false)}
            onSubScanSelect={onSubScanSelect}
          />
        )}

        <div style={{ flex: 1 }}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <DataGrid
              rows={filteredData}
              columns={updatedColumns}
              autoHeight
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              pageSizeOptions={[5, 10, 15]}
              disableRowSelectionOnClick
              localeText={customLocaleText}
            />
          )}
        </div>
      </Box>
    </div>
  );
}

export default SortableTable;